/**
 * Editor state for the flow designer (Phase -1, P-1.4).
 *
 * Holds:
 *   - current FlowDefinition (the frozen JSON shape, byte-identical to what
 *     the future backend will store in flow_versions.definition)
 *   - react-flow view model (nodes[] + edges[] with positions)
 *   - selection, dirty state, save state
 *
 * Converters:
 *   fromDefinition(def)  — builds react-flow nodes/edges from FlowDefinition
 *   toDefinition(canvas) — inverse; used on Save Draft
 */

import { create } from "zustand";
import type { Edge, Node as RFNode } from "@xyflow/react";
import type {
  ABTestNode,
  CheckoutPageNode,
  ConditionNode,
  DelayNode,
  FlowDefinition,
  FlowNode,
  FraudCheckNode,
  HttpRequestNode,
  NodeType,
  ScheduleNode,
  SwitchNode,
} from "../../types/flow";
import { arrangeLayout } from "./layout";

// ─── React-flow node data shape ──────────────────────────────────────────────

export interface CanvasNodeData extends Record<string, unknown> {
  nodeType: NodeType;
  payload: FlowNode;
  label: string;
}

export type CanvasNode = RFNode<CanvasNodeData>;
export type CanvasEdge = Edge;

// ─── Handles ─────────────────────────────────────────────────────────────────

/**
 * Per-node-type output-handle identifiers. The shape here is the source of
 * truth for both the card components (which render the handles) and the
 * converter (which translates `next` / `true_next` / etc. fields into edges).
 */
export const OUTPUT_HANDLES: Record<NodeType, readonly string[]> = {
  trigger: ["next"],
  condition: ["true_next", "false_next", "error_next"],
  switch: ["branches", "default_next", "error_next"],
  ab_test: ["variants", "error_next"],
  schedule: ["active_next", "inactive_next", "error_next"],
  checkout_page: ["next"],
  capture_policy: [],
  delay: ["next", "error_next"],
  fraud_check: ["low_next", "medium_next", "high_next", "error_next"],
  http_request: ["next", "error_next"],
};

// ─── Converters ──────────────────────────────────────────────────────────────

function labelFor(node: FlowNode): string {
  // User-set display name takes precedence; otherwise fall back to a
  // descriptive auto-label (used by react-flow's minimap + a11y).
  if (node.label && node.label.trim().length > 0) return node.label;
  switch (node.type) {
    case "trigger":
      return `Trigger · ${node.sources.length} source${node.sources.length === 1 ? "" : "s"}`;
    case "condition":
      return "Condition";
    case "switch":
      return `Switch · ${node.branches.length} branches`;
    case "ab_test":
      return `A/B · ${node.variants.map((v) => v.weight).join(" / ")}`;
    case "schedule":
      return "Schedule";
    case "checkout_page":
      return `Checkout: ${node.project_id}`;
    case "capture_policy":
      return `Capture: ${node.mode}`;
    case "delay":
      return node.duration.mode === "fixed"
        ? `Wait ${node.duration.iso8601}`
        : "Wait until …";
    case "fraud_check":
      return `Fraud: ${node.provider}`;
    case "http_request":
      return `HTTP ${node.method}`;
  }
}

/**
 * Lays out nodes in a simple column if they lack saved positions.
 * Positions are not part of FlowDefinition (not stored in the backend), so
 * we keep them in a parallel sidecar map under the same flow id.
 */
export interface NodePositions {
  [nodeId: string]: { x: number; y: number };
}

function defaultPosition(index: number): { x: number; y: number } {
  // Top-to-bottom column matches the canvas's flow direction (input on
  // top, outputs on bottom) and the auto-arrange button's direction so a
  // brand-new flow reads correctly without needing to click Arrange.
  // Step matches arrangeLayout's ROW_GAP for visual continuity.
  return { x: 200, y: 80 + index * 200 };
}

export function fromDefinition(
  def: FlowDefinition,
  positions: NodePositions = {},
): { nodes: CanvasNode[]; edges: CanvasEdge[] } {
  const nodes: CanvasNode[] = [];
  const edges: CanvasEdge[] = [];

  const entries = Object.entries(def.nodes);
  entries.forEach(([id, payload], idx) => {
    nodes.push({
      id,
      type: payload.type,
      position: positions[id] ?? defaultPosition(idx),
      data: {
        nodeType: payload.type,
        payload,
        label: labelFor(payload),
      },
    });

    // Edges per node type
    const push = (handle: string, target: string | null | undefined, style?: "error") => {
      if (!target) return;
      edges.push({
        id: `${id}:${handle}->${target}`,
        source: id,
        target,
        sourceHandle: handle,
        type: "deletable",
        animated: style !== "error",
        className: style === "error" ? "edge-error" : undefined,
      });
    };

    switch (payload.type) {
      case "trigger":
        push("next", payload.next);
        break;
      case "condition":
        push("true_next", payload.true_next);
        push("false_next", payload.false_next);
        push("error_next", payload.error_next, "error");
        break;
      case "switch":
        payload.branches.forEach((b, i) => push(`branch-${i}`, b.next));
        push("default_next", payload.default_next);
        push("error_next", payload.error_next, "error");
        break;
      case "ab_test":
        payload.variants.forEach((v) => push(`variant-${v.id}`, v.next));
        push("error_next", payload.error_next, "error");
        break;
      case "schedule":
        push("active_next", payload.active_next);
        push("inactive_next", payload.inactive_next);
        push("error_next", payload.error_next, "error");
        break;
      case "checkout_page":
        push("next", payload.next);
        break;
      case "capture_policy":
        break;
      case "delay":
        push("next", payload.next);
        push("error_next", payload.error_next, "error");
        break;
      case "fraud_check":
        push("low_next", payload.low_next);
        push("medium_next", payload.medium_next);
        push("high_next", payload.high_next);
        push("error_next", payload.error_next, "error");
        break;
      case "http_request":
        push("next", payload.next);
        push("error_next", payload.error_next, "error");
        break;
    }
  });

  return { nodes, edges };
}

// ─── Store ───────────────────────────────────────────────────────────────────

export interface EditorState {
  flowId: string | null;
  flowName: string;
  definition: FlowDefinition | null;
  positions: NodePositions;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  selectedNodeId: string | null;
  /**
   * The node whose configuration drawer is currently open. Distinct from
   * selectedNodeId — selection is for visual highlighting + keyboard delete;
   * drawer-open is for explicit edit. A single click selects; a double click
   * (or explicit "Configure" button) opens the drawer.
   */
  drawerNodeId: string | null;
  /**
   * State for the contextual "add node" menu — opened either by clicking the
   * "+" badge on a node's output handle (with a sourceContext) or by
   * right-clicking the empty canvas (no sourceContext).
   */
  addMenu: {
    open: boolean;
    /** Screen-space pixel position where the menu should anchor. */
    x: number;
    y: number;
    /** When set, the new node is auto-connected from this source handle. */
    sourceContext: { sourceId: string; sourceHandle: string } | null;
  };
  dirty: boolean;
  saving: boolean;

  // Actions
  loadFlow: (
    flowId: string,
    flowName: string,
    definition: FlowDefinition,
    positions?: NodePositions,
  ) => void;
  setNodes: (updater: (nodes: CanvasNode[]) => CanvasNode[]) => void;
  setEdges: (updater: (edges: CanvasEdge[]) => CanvasEdge[]) => void;
  selectNode: (id: string | null) => void;
  /** Open the right-side configuration drawer for a node (or close it with null). */
  openDrawer: (id: string | null) => void;
  /** Open the contextual add-node menu. */
  openAddMenu: (
    x: number,
    y: number,
    sourceContext: { sourceId: string; sourceHandle: string } | null,
  ) => void;
  closeAddMenu: () => void;
  markDirty: () => void;
  markSaving: (saving: boolean) => void;
  reset: () => void;

  /** Merge patch into an existing node's payload. Updates label + edges. */
  updateNode: (id: string, patch: Partial<FlowNode>) => void;
  /**
   * Strip an optional field from a node's payload entirely. Use this for
   * fields like `error_next` where setting `undefined` via updateNode would
   * leave the key present in the JSON. Cleans up any edge bound to the field.
   */
  removeNodeField: (id: string, field: string) => void;
  /** Create a new node at the given position. Returns the new id. */
  addNode: (type: NodeType, position?: { x: number; y: number }) => string;
  /** Delete a node and any incoming/outgoing edges; clean up `next`-style pointers on remaining nodes. */
  deleteNode: (id: string) => void;
  /** Add an edge from source.handle -> target (writes into the source node's pointer field). */
  connectEdge: (sourceId: string, sourceHandle: string, targetId: string) => void;
  /** Delete an edge (clears the source node's pointer field). */
  deleteEdge: (edgeId: string) => void;
  /** Recompute positions via BFS depth layout. Overwrites every node's position. */
  arrange: () => void;
}

export const useEditorStore = create<EditorState>((set) => ({
  flowId: null,
  flowName: "",
  definition: null,
  positions: {},
  nodes: [],
  edges: [],
  selectedNodeId: null,
  drawerNodeId: null,
  addMenu: { open: false, x: 0, y: 0, sourceContext: null },
  dirty: false,
  saving: false,

  loadFlow(flowId, flowName, definition, positions = {}) {
    const { nodes, edges } = fromDefinition(definition, positions);
    set({
      flowId,
      flowName,
      definition,
      positions,
      nodes,
      edges,
      selectedNodeId: null,
      drawerNodeId: null,
      addMenu: { open: false, x: 0, y: 0, sourceContext: null },
      dirty: false,
      saving: false,
    });
  },

  setNodes(updater) {
    set((s) => ({ nodes: updater(s.nodes), dirty: true }));
  },

  setEdges(updater) {
    set((s) => ({ edges: updater(s.edges), dirty: true }));
  },

  selectNode(id) {
    set({ selectedNodeId: id });
  },

  openDrawer(id) {
    if (id === null) {
      set({ drawerNodeId: null });
    } else {
      // Opening the drawer also visually selects the node so the highlight
      // ring matches what's being edited. Closing leaves selection alone.
      set({ drawerNodeId: id, selectedNodeId: id });
    }
  },

  openAddMenu(x, y, sourceContext) {
    set({ addMenu: { open: true, x, y, sourceContext } });
  },

  closeAddMenu() {
    set((s) => ({ addMenu: { ...s.addMenu, open: false, sourceContext: null } }));
  },

  markDirty() {
    set({ dirty: true });
  },

  markSaving(saving) {
    set({ saving });
  },

  reset() {
    set({
      flowId: null,
      flowName: "",
      definition: null,
      positions: {},
      nodes: [],
      edges: [],
      selectedNodeId: null,
      drawerNodeId: null,
      addMenu: { open: false, x: 0, y: 0, sourceContext: null },
      dirty: false,
      saving: false,
    });
  },

  updateNode(id, patch) {
    set((s) => {
      if (!s.definition) return s;
      const existing = s.definition.nodes[id];
      if (!existing) return s;
      const merged = { ...existing, ...patch } as FlowNode;
      const nextDef: FlowDefinition = {
        ...s.definition,
        nodes: { ...s.definition.nodes, [id]: merged },
      };
      const { nodes, edges } = rebuildCanvas(nextDef, s.positions);
      return { ...s, definition: nextDef, nodes, edges, dirty: true };
    });
  },

  removeNodeField(id, field) {
    set((s) => {
      if (!s.definition) return s;
      const existing = s.definition.nodes[id];
      if (!existing) return s;
      const stripped = { ...(existing as unknown as Record<string, unknown>) };
      delete stripped[field];
      const nextDef: FlowDefinition = {
        ...s.definition,
        nodes: { ...s.definition.nodes, [id]: stripped as unknown as FlowNode },
      };
      const { nodes, edges } = rebuildCanvas(nextDef, s.positions);
      return { ...s, definition: nextDef, nodes, edges, dirty: true };
    });
  },

  addNode(type, position) {
    const s = useEditorStore.getState();
    if (!s.definition) return "";
    const id = uid(NODE_ID_PREFIX[type]);
    const payload = defaultPayloadFor(type, id);
    const nextDef: FlowDefinition = {
      ...s.definition,
      nodes: { ...s.definition.nodes, [id]: payload },
    };
    const nextPositions: NodePositions = {
      ...s.positions,
      [id]: position ?? { x: 200, y: 200 },
    };
    const { nodes, edges } = rebuildCanvas(nextDef, nextPositions);
    useEditorStore.setState({
      ...s,
      definition: nextDef,
      positions: nextPositions,
      nodes,
      edges,
      dirty: true,
    });
    return id;
  },

  deleteNode(id) {
    set((s) => {
      if (!s.definition) return s;
      if (id === s.definition.start_node) return s; // don't delete the trigger
      const { [id]: _removed, ...remaining } = s.definition.nodes;
      // Clear any pointer fields on remaining nodes that pointed to this one.
      const cleaned: Record<string, FlowNode> = {};
      for (const [nid, node] of Object.entries(remaining)) {
        cleaned[nid] = clearPointersTo(node, id);
      }
      const nextDef: FlowDefinition = { ...s.definition, nodes: cleaned };
      const { [id]: _p, ...remainingPositions } = s.positions;
      const { nodes, edges } = rebuildCanvas(nextDef, remainingPositions);
      return {
        ...s,
        definition: nextDef,
        positions: remainingPositions,
        nodes,
        edges,
        selectedNodeId: s.selectedNodeId === id ? null : s.selectedNodeId,
        drawerNodeId: s.drawerNodeId === id ? null : s.drawerNodeId,
        dirty: true,
      };
    });
  },

  connectEdge(sourceId, sourceHandle, targetId) {
    set((s) => {
      if (!s.definition) return s;
      const source = s.definition.nodes[sourceId];
      const target = s.definition.nodes[targetId];
      if (!source || !target) return s;
      const updated = writePointer(source, sourceHandle, targetId);
      const nextDef: FlowDefinition = {
        ...s.definition,
        nodes: { ...s.definition.nodes, [sourceId]: updated },
      };
      const { nodes, edges } = rebuildCanvas(nextDef, s.positions);
      return { ...s, definition: nextDef, nodes, edges, dirty: true };
    });
  },

  arrange() {
    set((s) => {
      if (!s.definition) return s;
      const positions = arrangeLayout(s.definition);
      const { nodes, edges } = rebuildCanvas(s.definition, positions);
      return { ...s, positions, nodes, edges, dirty: true };
    });
  },

  deleteEdge(edgeId) {
    set((s) => {
      if (!s.definition) return s;
      const edge = s.edges.find((e) => e.id === edgeId);
      if (!edge || !edge.sourceHandle) return s;
      const source = s.definition.nodes[edge.source];
      if (!source) return s;
      const updated = writePointer(source, edge.sourceHandle, null);
      const nextDef: FlowDefinition = {
        ...s.definition,
        nodes: { ...s.definition.nodes, [edge.source]: updated },
      };
      const { nodes, edges } = rebuildCanvas(nextDef, s.positions);
      return { ...s, definition: nextDef, nodes, edges, dirty: true };
    });
  },
}));

// ─── Empty flow factory ──────────────────────────────────────────────────────

/**
 * Returns the definition for a brand-new flow — one trigger with no sources,
 * wired to a single unbound checkout_page node. Matches what the backend's
 * default-flow creator will produce (FLOWS.md § 5.1 default-flow lifecycle).
 */
export function blankDefinition(): FlowDefinition {
  return {
    schema_version: 1,
    start_node: "trig_1",
    nodes: {
      trig_1: {
        type: "trigger",
        sources: [],
        next: "page_1",
      },
      page_1: {
        type: "checkout_page",
        project_id: "",
        theme_id: "theme_default",
        layout: "single_page",
        payment_methods: ["credit-card"],
        allowed_countries: [],
        shipping_methods: [],
      },
    },
  };
}

// ─── Node factories (used by drag-from-palette) ─────────────────────────────

/**
 * Returns a default payload for a new node of the given type. Fields are
 * shaped to pass validator rule 3 (schema) out of the gate; merchant-critical
 * decisions (project_id, rule expressions, etc.) are empty and publish-block
 * until filled in.
 */
export function defaultPayloadFor(type: NodeType, id: string): FlowNode {
  switch (type) {
    case "trigger":
      return { type: "trigger", sources: [], next: "" };
    case "condition":
      return {
        type: "condition",
        rule: { "==": [{ var: "buyer_country" }, "FR"] },
        true_next: "",
        false_next: "",
      };
    case "switch":
      return {
        type: "switch",
        expr: { var: "currency" },
        branches: [],
        default_next: "",
      };
    case "ab_test":
      return {
        type: "ab_test",
        test_key: `ab_${id}`,
        variants: [
          { id: "A", weight: 50, next: "" },
          { id: "B", weight: 50, next: "" },
        ],
      };
    case "schedule":
      return {
        type: "schedule",
        window: {
          start: new Date().toISOString(),
          end: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString(),
        },
        active_next: "",
        inactive_next: "",
      };
    case "checkout_page":
      return {
        type: "checkout_page",
        project_id: "",
        theme_id: "theme_default",
        layout: "single_page",
        payment_methods: ["credit-card"],
        allowed_countries: [],
        shipping_methods: [],
      };
    case "capture_policy":
      return { type: "capture_policy", mode: "auto", next: null };
    case "delay":
      return {
        type: "delay",
        duration: { mode: "fixed", iso8601: "PT24H" },
        next: "",
      };
    case "fraud_check":
      return {
        type: "fraud_check",
        provider: "cost_plus",
        low_next: "",
        medium_next: "",
        high_next: "",
      };
    case "http_request":
      return {
        type: "http_request",
        method: "POST",
        url: "",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: "",
        timeout_ms: 10000,
        next: "",
      };
  }
}

// ─── Pointer field management ────────────────────────────────────────────────

/**
 * Writes the pointer field identified by `handle` on a node payload. Returns
 * a new payload object; does not mutate.
 */
function writePointer(
  node: FlowNode,
  handle: string,
  target: string | null,
): FlowNode {
  switch (node.type) {
    case "trigger":
      return handle === "next" ? { ...node, next: target ?? "" } : node;
    case "condition": {
      const c = node as ConditionNode;
      if (handle === "true_next") return { ...c, true_next: target ?? "" };
      if (handle === "false_next") return { ...c, false_next: target ?? "" };
      if (handle === "error_next")
        return target
          ? { ...c, error_next: target }
          : (() => {
              const { error_next: _e, ...rest } = c;
              return rest as ConditionNode;
            })();
      return c;
    }
    case "switch": {
      const s = node as SwitchNode;
      if (handle === "default_next") return { ...s, default_next: target ?? "" };
      if (handle === "error_next")
        return target
          ? { ...s, error_next: target }
          : (() => {
              const { error_next: _e, ...rest } = s;
              return rest as SwitchNode;
            })();
      if (handle.startsWith("branch-")) {
        const idx = Number.parseInt(handle.slice("branch-".length), 10);
        const branches = s.branches.map((b, i) =>
          i === idx ? { ...b, next: target ?? "" } : b,
        );
        return { ...s, branches };
      }
      return s;
    }
    case "ab_test": {
      const a = node as ABTestNode;
      if (handle === "error_next")
        return target
          ? { ...a, error_next: target }
          : (() => {
              const { error_next: _e, ...rest } = a;
              return rest as ABTestNode;
            })();
      if (handle.startsWith("variant-")) {
        const vid = handle.slice("variant-".length);
        const variants = a.variants.map((v) =>
          v.id === vid ? { ...v, next: target ?? "" } : v,
        );
        return { ...a, variants };
      }
      return a;
    }
    case "schedule": {
      const s = node as ScheduleNode;
      if (handle === "active_next") return { ...s, active_next: target ?? "" };
      if (handle === "inactive_next") return { ...s, inactive_next: target ?? "" };
      if (handle === "error_next")
        return target
          ? { ...s, error_next: target }
          : (() => {
              const { error_next: _e, ...rest } = s;
              return rest as ScheduleNode;
            })();
      return s;
    }
    case "checkout_page": {
      const p = node as CheckoutPageNode;
      if (handle === "next")
        return target ? { ...p, next: target } : (() => {
          const { next: _n, ...rest } = p;
          return rest as CheckoutPageNode;
        })();
      return p;
    }
    case "capture_policy":
      return node;
    case "delay": {
      const d = node as DelayNode;
      if (handle === "next") return { ...d, next: target ?? "" };
      if (handle === "error_next")
        return target
          ? { ...d, error_next: target }
          : (() => {
              const { error_next: _e, ...rest } = d;
              return rest as DelayNode;
            })();
      return d;
    }
    case "fraud_check": {
      const f = node as FraudCheckNode;
      if (handle === "low_next") return { ...f, low_next: target ?? "" };
      if (handle === "medium_next") return { ...f, medium_next: target ?? "" };
      if (handle === "high_next") return { ...f, high_next: target ?? "" };
      if (handle === "error_next")
        return target
          ? { ...f, error_next: target }
          : (() => {
              const { error_next: _e, ...rest } = f;
              return rest as FraudCheckNode;
            })();
      return f;
    }
    case "http_request": {
      const h = node as HttpRequestNode;
      if (handle === "next") return { ...h, next: target ?? "" };
      if (handle === "error_next")
        return target
          ? { ...h, error_next: target }
          : (() => {
              const { error_next: _e, ...rest } = h;
              return rest as HttpRequestNode;
            })();
      return h;
    }
  }
}

/**
 * Clears any pointer fields on the given node that reference `targetId`.
 * Used when a node is deleted — prevents dangling pointers.
 */
function clearPointersTo(node: FlowNode, targetId: string): FlowNode {
  const handles = OUTPUT_HANDLES[node.type];
  let next: FlowNode = node;
  for (const handleGroup of handles) {
    if (handleGroup === "branches") {
      if (next.type === "switch") {
        const s = next as SwitchNode;
        next = {
          ...s,
          branches: s.branches.map((b) => (b.next === targetId ? { ...b, next: "" } : b)),
        };
      }
      continue;
    }
    if (handleGroup === "variants") {
      if (next.type === "ab_test") {
        const a = next as ABTestNode;
        next = {
          ...a,
          variants: a.variants.map((v) => (v.next === targetId ? { ...v, next: "" } : v)),
        };
      }
      continue;
    }
    // Generic pointer fields — loop over named handles and use writePointer.
    const current = (next as unknown as Record<string, unknown>)[handleGroup];
    if (current === targetId) {
      next = writePointer(next, handleGroup, null);
    }
  }
  return next;
}

// ─── Rebuild canvas nodes/edges from current definition + positions ─────────

function rebuildCanvas(
  definition: FlowDefinition,
  positions: NodePositions,
): { nodes: CanvasNode[]; edges: CanvasEdge[] } {
  return fromDefinition(definition, positions);
}

function uid(prefix: string): string {
  // Short random id suitable for FlowDefinition node keys.
  const bytes = crypto.getRandomValues(new Uint8Array(4));
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${prefix}_${hex}`;
}

const NODE_ID_PREFIX: Record<NodeType, string> = {
  trigger: "trig",
  condition: "cond",
  switch: "swi",
  ab_test: "ab",
  schedule: "sch",
  checkout_page: "page",
  capture_policy: "cap",
  delay: "delay",
  fraud_check: "fraud",
  http_request: "http",
};
